import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useBackendApi from '../../../../../hooks/useBackendApi';
import { RequestOptions } from '../../../../../types/httpRequests';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;
const CALENDAR_CONNECT_ERROR_KEY = 'calendar_connect_error';

type CalendarStatus = {
  connected: boolean;
  provider_email?: string;
};

type CalendarApiBody = {
  status_code?: number;
  message?: string;
  detail?: string;
};

type CalendarStatusResponse = CalendarApiBody & {
  connected?: boolean;
  provider_email?: string;
};

type CalendarConnectResponse = CalendarApiBody & {
  auth_url?: string;
};

type CalendarDisconnectResponse = CalendarApiBody;

type ApiFetchRequest = (options: RequestOptions) => Promise<{
  status: number;
  body?: unknown;
  isError?: boolean;
} | null>;

type CalendarApiResult<TBody extends CalendarApiBody> = {
  status: number;
  body?: TBody;
  isError: boolean;
  unauthorized: boolean;
};

const isUnauthorizedResponse = (status?: number, body?: CalendarApiBody): boolean =>
  status === 401 ||
  body?.status_code === 401 ||
  body?.message === 'UNAUTHENTICATED' ||
  body?.message === 'UNAUTHORIZED' ||
  body?.message === 'NO_ACCESS';

const runCalendarRequest = async <TBody extends CalendarApiBody>(
  apiFetchRequest: ApiFetchRequest,
  options: RequestOptions,
): Promise<CalendarApiResult<TBody> | null> => {
  const res = await apiFetchRequest(options);
  if (!res) {
    return null;
  }

  const body = res.body as TBody | undefined;

  return {
    status: res.status,
    body,
    isError: Boolean(res.isError),
    unauthorized: isUnauthorizedResponse(res.status, body),
  };
};

const useCalendarIntegration = () => {
  const { apiFetchRequest } = useBackendApi();
  const navigate = useNavigate();

  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus | null>(null);
  const [calendarActionError, setCalendarActionError] = useState<string | null>(null);
  const [calendarActionLoading, setCalendarActionLoading] = useState(false);
  const handleUnauthorized = useCallback(() => navigate('/401.html'), [navigate]);

  const fetchCalendarStatus = useCallback(async () => {
    try {
      const res = await runCalendarRequest<CalendarStatusResponse>(apiFetchRequest, {
        endpoint: `${backendURL}/integrations/google/status`,
        method: 'GET',
      });

      if (!res) {
        setCalendarStatus({ connected: false });
        return;
      }

      if (res.unauthorized) {
        handleUnauthorized();
        return;
      }

      if (res.isError || res.status !== 200) {
        setCalendarStatus({ connected: false });
        return;
      }

      setCalendarStatus({
        connected: Boolean(res.body?.connected),
        provider_email: res.body?.provider_email,
      });
    } catch {
      setCalendarStatus({ connected: false });
    }
  }, [apiFetchRequest, handleUnauthorized]);

  useEffect(() => {
    const callbackError = localStorage.getItem(CALENDAR_CONNECT_ERROR_KEY);
    if (callbackError) {
      localStorage.removeItem(CALENDAR_CONNECT_ERROR_KEY);
      setCalendarActionError('Error connecting to Google Calendar. Please try again later.');
    }

    void fetchCalendarStatus();
  }, [fetchCalendarStatus]);

  const handleConnectCalendar = useCallback(async () => {
    setCalendarActionError(null);
    setCalendarActionLoading(true);

    try {
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      localStorage.setItem('calendar_connect_return_to', returnTo);

      const res = await runCalendarRequest<CalendarConnectResponse>(apiFetchRequest, {
        endpoint: `${backendURL}/integrations/google/connect`,
        method: 'GET',
      });

      if (!res) {
        setCalendarActionError('Unable to start Google Calendar connection.');
        return;
      }

      if (res.unauthorized) {
        handleUnauthorized();
        return;
      }

      if (res.isError || res.status !== 200) {
        setCalendarActionError('Unable to start Google Calendar connection.');
        return;
      }

      const authUrl = res.body?.auth_url;
      if (!authUrl || typeof authUrl !== 'string') {
        setCalendarActionError('Unable to start Google Calendar connection.');
        return;
      }

      window.location.assign(authUrl);
    } catch {
      setCalendarActionError('Unable to start Google Calendar connection.');
    } finally {
      setCalendarActionLoading(false);
    }
  }, [apiFetchRequest, handleUnauthorized]);

  const handleDisconnectCalendar = useCallback(async () => {
    setCalendarActionError(null);
    setCalendarActionLoading(true);

    try {
      const res = await runCalendarRequest<CalendarDisconnectResponse>(apiFetchRequest, {
        endpoint: `${backendURL}/integrations/google/disconnect`,
        method: 'POST',
      });

      if (!res) {
        setCalendarActionError('Unable to disconnect Google Calendar.');
        return;
      }

      if (res.unauthorized) {
        handleUnauthorized();
        return;
      }

      if (res.isError || res.status !== 200) {
        setCalendarActionError('Unable to disconnect Google Calendar.');
        return;
      }

      await fetchCalendarStatus();
    } catch {
      setCalendarActionError('Unable to disconnect Google Calendar.');
    } finally {
      setCalendarActionLoading(false);
    }
  }, [apiFetchRequest, fetchCalendarStatus, handleUnauthorized]);

  const clearCalendarActionError = useCallback(() => setCalendarActionError(null), []);

  return {
    calendarStatus,
    calendarActionError,
    calendarActionLoading,
    fetchCalendarStatus,
    handleConnectCalendar,
    handleDisconnectCalendar,
    setCalendarActionError,
    clearCalendarActionError,
  };
};

export default useCalendarIntegration;
